import React, { useState } from 'react';
import { Card, Button, Space, message, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, ExportOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useUnitsManagement } from './hooks/useUnitsManagement';
import UnitsFilters from './components/UnitsFilters';
import UnitsTable from './components/UnitsTable';
import UnitsForm from './components/UnitsForm';

/**
 * UnitsManagementPage - หน้าหลักจัดการหน่วยนับ
 * รองรับ CRUD operations, Search, Filter, Bulk actions
 */
const UnitsManagementPage = () => {
  const { hasPermission } = useAuth();
  
  // State สำหรับ Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [viewingUnit, setViewingUnit] = useState(null);

  // ใช้ custom hook สำหรับจัดการข้อมูล
  const {
    // Data
    tableData,
    categories,
    statistics,
    
    // UI State
    loading,
    pagination,
    filters,
    sorter,
    selectedRowKeys,
    
    // Actions
    createUnit,
    updateUnit,
    deleteUnit,
    deleteMultipleUnits,
    refreshData,
    
    // Handlers
    handleFilterChange,
    handleTableChange,
    resetFilters,
    setSelectedRowKeys
  } = useUnitsManagement();

  // ตรวจสอบสิทธิ์
  const canRead = hasPermission('units', 'read');
  const canCreate = hasPermission('units', 'create');
  const canUpdate = hasPermission('units', 'update');
  const canDelete = hasPermission('units', 'delete');

  // หากไม่มีสิทธิ์อ่าน ให้แสดงข้อความ
  if (!canRead) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h3>คุณไม่มีสิทธิ์เข้าถึงหน้านี้</h3>
          <p>กรุณาติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์ในการจัดการหน่วยนับ</p>
        </div>
      </Card>
    );
  }

  /**
   * เปิด Modal เพิ่มหน่วยใหม่
   */
  const handleAdd = () => {
    if (!canCreate) {
      message.warning('คุณไม่มีสิทธิ์เพิ่มหน่วยนับใหม่');
      return;
    }
    setEditingUnit(null);
    setModalVisible(true);
  };

  /**
   * เปิด Modal แก้ไขหน่วย
   */
  const handleEdit = (unit) => {
    if (!canUpdate) {
      message.warning('คุณไม่มีสิทธิ์แก้ไขหน่วยนับ');
      return;
    }
    setEditingUnit(unit);
    setModalVisible(true);
  };

  /**
   * ดูรายละเอียดหน่วย
   */
  const handleView = (unit) => {
    setViewingUnit(unit);
    setEditingUnit(unit);
    setModalVisible(true);
  };

  /**
   * ลบหน่วยเดี่ยว
   */
  const handleDelete = async (unitId) => {
    if (!canDelete) {
      message.warning('คุณไม่มีสิทธิ์ลบหน่วยนับ');
      return;
    }

    try {
      await deleteUnit(unitId);
    } catch (error) {
      // Error message จะแสดงใน deleteUnit function แล้ว
    }
  };

  /**
   * ลบหน่วยหลายรายการ
   */
  const handleBulkDelete = () => {
    if (!canDelete) {
      message.warning('คุณไม่มีสิทธิ์ลบหน่วยนับ');
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.warning('กรุณาเลือกหน่วยนับที่ต้องการลบ');
      return;
    }

    Modal.confirm({
      title: 'ยืนยันการลบ',
      content: (
        <div>
          <p>ต้องการลบหน่วยนับ <strong>{selectedRowKeys.length}</strong> รายการ ใช่หรือไม่?</p>
          <p style={{ color: '#ff4d4f', fontSize: '14px' }}>
            หน่วยนับที่กำลังใช้งานจะถูกปิดการใช้งานแทนการลบ
          </p>
        </div>
      ),
      okText: 'ลบ',
      cancelText: 'ยกเลิก',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMultipleUnits(selectedRowKeys);
        } catch (error) {
          // Error message จะแสดงใน deleteMultipleUnits function แล้ว
        }
      }
    });
  };

  /**
   * บันทึกข้อมูลหน่วย (Create/Update)
   */
  const handleSave = async (unitData) => {
    try {
      if (editingUnit && !viewingUnit) {
        // แก้ไข
        await updateUnit(editingUnit.id, unitData);
      } else if (!viewingUnit) {
        // สร้างใหม่
        await createUnit(unitData);
      }
      
      setModalVisible(false);
      setEditingUnit(null);
      setViewingUnit(null);
    } catch (error) {
      // Error message จะแสดงใน createUnit/updateUnit function แล้ว
    }
  };

  /**
   * ปิด Modal
   */
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingUnit(null);
    setViewingUnit(null);
  };

  /**
   * ส่งออกข้อมูล (Export)
   */
  const handleExport = () => {
    message.info('ฟีเจอร์ส่งออกข้อมูลจะพัฒนาในเวอร์ชันถัดไป');
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Card 
        size="small"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
              จัดการหน่วยนับ
            </h2>
            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
              เพิ่ม แก้ไข และจัดการหน่วยนับทั้งหมดในระบบ
            </p>
          </div>
          
          <Space wrap>
            {/* ปุ่มลบหลายรายการ */}
            {canDelete && selectedRowKeys.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                disabled={loading}
              >
                ลบ {selectedRowKeys.length} รายการ
              </Button>
            )}
            
            {/* ปุ่มส่งออก */}
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              disabled={loading}
            >
              ส่งออกข้อมูล
            </Button>
            
            {/* ปุ่มเพิ่ม */}
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={loading}
              >
                เพิ่มหน่วยนับใหม่
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* Filters */}
      <UnitsFilters
        filters={filters}
        categories={categories}
        statistics={statistics}
        loading={loading}
        onFilterChange={handleFilterChange}
        onReset={resetFilters}
        onRefresh={refreshData}
      />

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <UnitsTable
          dataSource={tableData}
          loading={loading}
          pagination={pagination}
          sorter={sorter}
          selectedRowKeys={selectedRowKeys}
          onTableChange={handleTableChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={canUpdate ? undefined : handleView} // ถ้าแก้ไขไม่ได้ให้ใช้ View
          onSelectionChange={canDelete ? setSelectedRowKeys : undefined}
          canEdit={canUpdate}
          canDelete={canDelete}
        />
      </Card>

      {/* Modal Form */}
      <UnitsForm
        visible={modalVisible}
        loading={loading}
        editingUnit={editingUnit}
        viewOnly={!!viewingUnit}
        categories={categories}
        onSave={handleSave}
        onCancel={handleModalClose}
      />
    </div>
  );
};

export default UnitsManagementPage;